import classNames from "classnames";
import * as React from "react";

import styles from "./Spotlight.module.css";

interface SpotlightProps extends React.PropsWithChildren {
  /**
   * Unique identifier to trigger a spotlight animation.
   * When it changes to a new truthy value, the animation is triggered.
   **/
  spotlightKey: number | undefined;

  /** Optional class name to apply to the root element. */
  className?: classNames.Argument;
}

/**
 * Component that spotlights its children with a pulsing animation
 * when the spotlightKey changes. Also scrolls the element into
 * view when spotlighted.
 *
 * Intended to bring attention to a specific section
 * of a panel's settings based on a trigger that sets
 * the spotlightKey.
 */
export function Spotlight({
  children,
  className,
  spotlightKey,
}: SpotlightProps) {
  const ref = useSpotlightSection(spotlightKey);

  React.useEffect(
    function scrollToElement() {
      const el = ref.current;

      if (!el || !spotlightKey) {
        return;
      }

      // Scroll to the element smoothly while minimizing the scroll distance
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    },
    [spotlightKey, ref],
  );

  return (
    <div className={classNames(className)} ref={ref}>
      {children}
    </div>
  );
}

/**
 * Attaches a pulsing spotlight animation to a div.
 *
 * @param key on change, the animation is triggered.
 * @returns ref to attach to the element.
 */
function useSpotlightSection(key: number | undefined) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!ref.current || !key) {
      return;
    }

    const el = ref.current;

    const observer = new IntersectionObserver(
      ([entry], observerInstance) => {
        if (entry.isIntersecting) {
          el.classList.remove(styles.spotlight);

          // Forces a browser reflow to re-trigger the animation
          void el.offsetWidth;

          el.classList.add(styles.spotlight);

          // We don't need to observe the element anymore
          // once animation is triggered
          observerInstance.disconnect();
        }
      },
      {
        /**
         * Trigger the animation when the element is 99% in view.
         *
         * This is so we don't trigger the animation before
         * the user has a chance to see the element.
         *
         * But it's also not set to 100% because the browser's intersection
         * observer can be finicky and not always register the element
         * as intersecting at the 100% mark but rather at 99.98%.
         */
        threshold: 0.99,
      },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [key]);

  return ref;
}
